import React from 'react';
import { AlertTriangle, TrendingUp } from 'lucide-react';

const RateBadge = ({ leaveBalance, formatDate }) => {
  const d = leaveBalance.rate_change_date;
  if (!d || Number(d.slice(0, 4)) !== leaveBalance.year) return null;
  const past = new Date(d) <= new Date();
  return (
    <div className="mt-2 flex items-start gap-1.5 rounded-md bg-emerald-50 border border-emerald-200 px-2 py-1.5 text-xs text-emerald-800" data-testid="rate-change-badge">
      <TrendingUp className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
      <span>
        {past ? 'Rate moved' : 'Rate moves'} from 2 to <b>2.5 days/month</b> on {formatDate(d)} (1-year anniversary).
        This year's entitlement is split at that date.
      </span>
    </div>
  );
};

const LeaveBalanceSection = ({ leaveBalance }) => {
  if (!leaveBalance) return null;
  
  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  
  return (
    <div className="mt-6 space-y-4">
      <h3 className="font-semibold">Leave Balance {leaveBalance.year}</h3>
      
      {/* Notice for Resigned/Terminated Employees */}
      {leaveBalance.is_resigned_terminated && leaveBalance.last_working_date && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-800">
            <p className="font-medium">Pro-rated Entitlement</p>
            <p>Last working date: {formatDate(leaveBalance.last_working_date)}</p>
            <p className="text-amber-600">Entitlement calculated based on actual working days.</p>
          </div>
        </div>
      )}
      
      {/* Annual Leave Breakdown */}
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
        <p className="text-sm font-medium text-blue-800 mb-2">Annual Leave</p>
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span>Annual Entitlement{leaveBalance.is_resigned_terminated ? ' (Pro-rated)' : ''}:</span>
            <span>{(leaveBalance.annual_entitlement || 30).toFixed(1)} days</span>
          </div>
          {leaveBalance.carry_forward_from_previous > 0 && (
            <div className="flex justify-between text-sm text-green-600">
              <span>+ Carry Forward (max 15):</span>
              <span className="font-medium">{leaveBalance.carry_forward_from_previous.toFixed(1)} days</span>
            </div>
          )}
          {leaveBalance.carry_forward_from_previous < 0 && (
            <div className="flex justify-between text-sm text-red-600">
              <span>Carry Forward (advance paid):</span>
              <span className="font-medium">{leaveBalance.carry_forward_from_previous.toFixed(1)} days</span>
            </div>
          )}
          {leaveBalance.carry_forward_lapsed > 0 && (
            <div className="flex justify-between text-sm text-orange-600">
              <span>Lapsed (exceeded 15-day limit):</span>
              <span className="font-medium">{leaveBalance.carry_forward_lapsed.toFixed(1)} days</span>
            </div>
          )}
          {!!leaveBalance.manual_adjustment && (
            <div className={`flex justify-between text-sm ${leaveBalance.manual_adjustment < 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="balance-manual-adjustment">
              <span>{leaveBalance.manual_adjustment < 0 ? 'Extra paid (advance):' : 'Manual adjustment:'}</span>
              <span className="font-medium">{leaveBalance.manual_adjustment > 0 ? '+' : ''}{leaveBalance.manual_adjustment.toFixed(1)} days</span>
            </div>
          )}
          <div className="border-t border-blue-300 pt-1 mt-1">
            <div className="flex justify-between text-sm font-medium">
              <span>Total Available:</span>
              <span>{(leaveBalance.annual_total || (leaveBalance.annual_entitlement || 30) + leaveBalance.carry_forward_from_previous).toFixed(1)} days</span>
            </div>
          </div>
          <div className="flex justify-between text-sm text-red-600">
            <span>- Taken:</span>
            <span>{leaveBalance.annual_taken.toFixed(1)} days</span>
          </div>
          <div className="border-t border-blue-300 pt-1 mt-1">
            <div className={`flex justify-between text-lg font-bold ${leaveBalance.annual_balance < 0 ? 'text-red-600' : 'text-blue-800'}`}>
              <span>Balance:</span>
              <span data-testid="annual-balance-value">{leaveBalance.annual_balance.toFixed(1)} days</span>
            </div>
          </div>
          {leaveBalance.carry_forward_to_next > 0 && (
            <div className="flex justify-between text-xs text-gray-500 mt-2 pt-2 border-t border-blue-200">
              <span>Can carry to next year:</span>
              <span>{leaveBalance.carry_forward_to_next.toFixed(1)} days (max 15)</span>
            </div>
          )}
          <RateBadge leaveBalance={leaveBalance} formatDate={formatDate} />
        </div>
      </div>

      {/* Used Breakdown: Annual vs Partial-paid vs LOP */}
      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200" data-testid="used-breakdown">
        <p className="text-sm font-medium text-slate-800 mb-2">Used Breakdown</p>
        <div className="space-y-1.5">
          <div className="flex justify-between text-sm">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> Annual (paid):</span>
            <span className="font-medium">{(leaveBalance.annual_pure_taken || 0).toFixed(1)} days</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-violet-500 inline-block" /> Partial-paid (paid portion):</span>
            <span className="font-medium">{(leaveBalance.partial_paid_taken || 0).toFixed(1)} days</span>
          </div>
          <div className="flex justify-between text-sm text-red-600">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> LOP (loss of pay):</span>
            <span className="font-medium">{(leaveBalance.lop_days || 0).toFixed(1)} days</span>
          </div>
          {(leaveBalance.mgmt_unpaid_days || 0) > 0 && (
            <div className="flex justify-between text-xs text-amber-700 pt-1 mt-1 border-t border-slate-200">
              <span>MGMT unpaid (salary only · not LOP):</span>
              <span className="font-medium">{leaveBalance.mgmt_unpaid_days.toFixed(1)} days</span>
            </div>
          )}
          <div className="flex justify-between text-xs text-gray-500 pt-1.5 mt-1 border-t border-slate-200">
            <span>Total annual pool used:</span>
            <span className="font-semibold">{(leaveBalance.annual_taken || 0).toFixed(1)} days</span>
          </div>
        </div>
      </div>

      {/* Other Leave Types */}
      <div className="space-y-2 pt-2">
        <p className="text-sm font-medium text-gray-700">Other Leave Types Used</p>
        <div className="flex justify-between text-sm">
          <span>Sick Leave:</span>
          <span>{leaveBalance.sick_taken.toFixed(1)} days</span>
        </div>
        <div className="flex justify-between text-sm">
          <span>Emergency:</span>
          <span>{leaveBalance.emergency_taken.toFixed(1)} days</span>
        </div>
        {leaveBalance.maternity_taken > 0 && (
          <div className="flex justify-between text-sm">
            <span>Maternity:</span>
            <span>{leaveBalance.maternity_taken.toFixed(1)} days</span>
          </div>
        )}
        {leaveBalance.paternity_taken > 0 && (
          <div className="flex justify-between text-sm">
            <span>Paternity:</span>
            <span>{leaveBalance.paternity_taken.toFixed(1)} days</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveBalanceSection;
